import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, OnboardingTooltip, Prompt, PromptForm, Toast } from "@/shared/ui";
import StepsSwitcher from "@/shared/ui/StepsSwitcher";
import { extractErrorMessage, getWorkerConflictKey, HR_EMPLOYEES_PAGE_ROUTE, IIN_RE, EMAIL_RE, isPhoneComplete, normalizePhone } from "@/shared/utils";
import { useAppDispatch, useAuth, useFieldAvailability } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { useCreateImportDraftMutation, useUpdateImportDraftMutation, useGetImportDraftQuery, useUploadImportDraftFileMutation, useDeleteImportDraftFileMutation, importDraftsApi } from "../../api/importDrafts";
import type { ContractTab, DocumentTab, ImportWorkerFormValues, ImportWorkerSectionId } from "./types";
import { createImportWorkerSchema } from "./validation";
import { useImportWorkerMutation } from "./api";
import { IMPORT_SECTIONS, DEFAULT_FORM_VALUES, SECTION_FIELDS, FLAT_STEPS, CONTRACT_TAB_FIELDS, DOCUMENT_TAB_FIELDS } from "./consts";
import { useFormLeaveProtection, useImportDraftRestoration } from "./hooks";
import { ImportWorkerPageSkeleton } from "./components";
import { PersonalSection, ContractSection, BankingSection, DocumentsSection } from "./sections";
import { SINGLE_FILE_FIELDS, MULTI_FILE_FIELDS, buildImportFormData, extractScalarData } from "./utils";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}


function getFlatIndex(section: ImportWorkerSectionId, contractTab: ContractTab, documentsTab: DocumentTab): number {
  if (section === "personal") return 0;
  if (section === "contract") {
    if (contractTab === "info") return 1;
    if (contractTab === "position") return 2;
    return 3;
  }
  if (section === "banking") return 4;
  if (documentsTab === "contract") return 5;
  return 6;
}

export default function ImportWorkerPage() {
  const { t } = useTranslation("ImportWorkerPage");
  const { t: tCommon } = useTranslation("Common");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftIdParam = searchParams.get("draftId");
  const draftId = draftIdParam ? Number(draftIdParam) : null;

  const { data: draftData, isLoading: isDraftLoading } = useGetImportDraftQuery(draftId!, { skip: draftId == null });

  const draftFilenames = useMemo(() => {
    if (!draftData?.files) return {};
    const map: Record<string, string> = {};
    for (const f of draftData.files) {
      const key = f.file_index != null ? `${f.field_name}[${f.file_index}]` : f.field_name;
      map[key] = f.original_filename;
    }
    return map;
  }, [draftData]);

  const schema = useMemo(() => createImportWorkerSchema(draftFilenames), [draftFilenames]);

  const methods = useForm<ImportWorkerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onSubmit",
  });

  const { handleSubmit, watch, formState, trigger } = methods;

  const [activeSection, setActiveSection] = useState<ImportWorkerSectionId>("personal");
  const [contractTab, setContractTab] = useState<ContractTab>("info");
  const [documentsTab, setDocumentsTab] = useState<DocumentTab>("contract");

  const { draftRestoredRef } = useImportDraftRestoration({
    draftData,
    methods,
    setActiveSection,
    setContractTab,
    setDocumentsTab,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  const { hasPermission } = useAuth();
  const canSaveDraft = hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);

  const [importWorker] = useImportWorkerMutation();
  const [createDraft] = useCreateImportDraftMutation();
  const [updateDraft] = useUpdateImportDraftMutation();
  const [uploadDraftFile] = useUploadImportDraftFileMutation();
  const [deleteDraftFile] = useDeleteImportDraftFileMutation();
  const leaveProtection = useFormLeaveProtection(methods, isSubmitted);

  const draftIdRef = useRef<number | null>(draftId);
  const createDraftPromiseRef = useRef<Promise<number> | null>(null);
  const hadInitialDraftRef = useRef(draftId != null);
  const uploadedFilesRef = useRef<Record<string, File>>({});
  const sectionStateRef = useRef({ activeSection, contractTab, documentsTab });

  useEffect(function syncSectionStateRef() {
    sectionStateRef.current = { activeSection, contractTab, documentsTab };
  }, [activeSection, contractTab, documentsTab]);

  useEffect(function syncDraftIdRef() {
    if (draftId != null && draftIdRef.current == null) draftIdRef.current = draftId;
  }, [draftId]);

  const buildScalarPayload = useCallback(function buildScalarPayload() {
    const values = methods.getValues();
    const s = sectionStateRef.current;
    return {
      last_section: s.activeSection,
      last_contract_tab: s.contractTab,
      last_documents_tab: s.documentsTab,
      scalar_data: extractScalarData(values),
    };
  }, [methods]);

  const ensureDraftId = useCallback(
    async function ensureDraftId(payload: ReturnType<typeof buildScalarPayload>): Promise<number> {
      if (draftIdRef.current != null) return draftIdRef.current;
      if (!createDraftPromiseRef.current) {
        createDraftPromiseRef.current = createDraft(payload)
          .unwrap()
          .then(function onCreated(created) {
            draftIdRef.current = created.id;
            draftRestoredRef.current = true;
            setSearchParams({ draftId: String(created.id) }, { replace: true });
            return created.id;
          })
          .catch(function onCreateError(err) {
            createDraftPromiseRef.current = null;
            throw err;
          });
      }
      return createDraftPromiseRef.current;
    },
    [createDraft, setSearchParams, draftRestoredRef],
  );

  const syncFiles = useCallback(
    async function syncFiles(currentDraftId: number) {
      const values = methods.getValues();
      const serverFiles = draftData?.files ?? [];
      const ops: Promise<unknown>[] = [];

      for (const field of SINGLE_FILE_FIELDS) {
        const file = values[field];
        if (file instanceof File) {
          if (uploadedFilesRef.current[field] !== file) {
            ops.push(
              uploadDraftFile({ draftId: currentDraftId, field_name: field, file })
                .unwrap()
                .then(function markUploaded() {
                  uploadedFilesRef.current[field] = file;
                }),
            );
          }
        } else if (uploadedFilesRef.current[field]) {
          ops.push(
            deleteDraftFile({ draftId: currentDraftId, field_name: field })
              .unwrap()
              .catch(function ignoreMissing() {})
              .then(function markRemoved() {
                delete uploadedFilesRef.current[field];
              }),
          );
        }
      }

      for (const field of MULTI_FILE_FIELDS) {
        const files = (values[field] as (File | null)[] | undefined) ?? [];
        files.forEach(function uploadEntry(f, i) {
          const key = `${field}[${i}]`;
          if (f instanceof File && uploadedFilesRef.current[key] !== f) {
            ops.push(
              uploadDraftFile({ draftId: currentDraftId, field_name: field, file: f, file_index: i })
                .unwrap()
                .then(function markUploaded() {
                  uploadedFilesRef.current[key] = f;
                }),
            );
          }
        });
        const knownIndices = new Set<number>();
        for (const sf of serverFiles) {
          if (sf.field_name === field) knownIndices.add(sf.file_index ?? 0);
        }
        for (const key of Object.keys(uploadedFilesRef.current)) {
          if (key.startsWith(`${field}[`)) {
            const match = key.match(/\[(\d+)\]$/);
            if (match) knownIndices.add(Number(match[1]));
          }
        }
        knownIndices.forEach(function deleteStale(idx) {
          if (idx >= files.length) {
            ops.push(
              deleteDraftFile({ draftId: currentDraftId, field_name: field, file_index: idx })
                .unwrap()
                .catch(function ignoreMissing() {})
                .then(function markRemoved() {
                  delete uploadedFilesRef.current[`${field}[${idx}]`];
                }),
            );
          }
        });
      }

      if (ops.length > 0) {
        await Promise.allSettled(ops);
        dispatch(importDraftsApi.util.invalidateTags([{ type: "ImportWorkerDrafts", id: currentDraftId }]));
      }
    },
    [methods, draftData, uploadDraftFile, deleteDraftFile, dispatch],
  );

  const persistDraft = useCallback(
    async function persistDraft(withFiles: boolean) {
      if (!canSaveDraft) return;
      try {
        setIsSavingDraft(true);
        setDraftSaveStatus("saving");
        const payload = buildScalarPayload();
        const id = await ensureDraftId(payload);
        await updateDraft({ id, ...payload }).unwrap();
        if (withFiles) await syncFiles(id);
        methods.reset(methods.getValues(), {
          keepErrors: true,
          keepIsSubmitted: true,
          keepTouched: true,
        });
        setDraftSaveStatus("saved");
      } catch {
        setDraftSaveStatus("error");
      } finally {
        setIsSavingDraft(false);
      }
    },
    [canSaveDraft, buildScalarPayload, ensureDraftId, updateDraft, syncFiles, methods],
  );

  const persistRef = useRef(persistDraft);
  useEffect(function syncPersistRef() {
    persistRef.current = persistDraft;
  }, [persistDraft]);

  const handleSaveDraft = useCallback(
    function handleSaveDraft() {
      if (isSavingDraft) return;
      void persistDraft(true);
    },
    [isSavingDraft, persistDraft],
  );

  const handleDraftStatusReset = useCallback(function handleDraftStatusReset() {
    setDraftSaveStatus("idle");
  }, []);

  useEffect(
    function flushOnSectionChange() {
      if (!canSaveDraft) return;
      if (hadInitialDraftRef.current && !draftRestoredRef.current) return;
      if (!methods.formState.isDirty) return;
      void persistRef.current(true);
    },
    [activeSection, contractTab, documentsTab, canSaveDraft, methods],
  );

  const flatIndex = getFlatIndex(activeSection, contractTab, documentsTab);
  const isFirstStep = flatIndex === 0;
  const isLastStep = flatIndex === FLAT_STEPS.length - 1;

  const translatedSections = useMemo(
    () => IMPORT_SECTIONS.map((s) => ({ ...s, label: t(s.label) })),
    [t],
  );

  const watchedValues = watch();

  const iinValue = watchedValues.iin ?? "";
  const emailValue = watchedValues.email ?? "";
  const phoneValue = watchedValues.phone ?? "";

  const iinAvailability = useFieldAvailability({
    resource: "worker",
    field: "iin",
    value: iinValue,
    isFormatValid: IIN_RE.test(iinValue),
  });
  const emailAvailability = useFieldAvailability({
    resource: "worker",
    field: "email",
    value: emailValue,
    isFormatValid: EMAIL_RE.test(emailValue),
  });
  const phoneAvailability = useFieldAvailability({
    resource: "worker",
    field: "phone",
    value: normalizePhone(phoneValue),
    isFormatValid: isPhoneComplete(phoneValue),
  });
  const contractNumberValue = watchedValues.contract_number ?? "";
  const contractNumberAvailability = useFieldAvailability({
    resource: "contract",
    field: "contract_number",
    value: contractNumberValue,
    isFormatValid: /^\d{1,18}$/.test(contractNumberValue) && Number(contractNumberValue) > 0,
  });
  const contractNumberError =
    contractNumberAvailability.state === "taken" ? t("validation.contract_number_taken") : undefined;

  const hasTakenField =
    iinAvailability.state === "taken" ||
    emailAvailability.state === "taken" ||
    phoneAvailability.state === "taken" ||
    contractNumberAvailability.state === "taken";

  const completedSteps = useMemo(() => {
    const completed: string[] = [];
    const gender = watchedValues.gender;
    const isStudent = watchedValues.is_student;
    const noEducation = watchedValues.has_no_education;
    const noExperience = watchedValues.has_no_work_experience;

    for (const [sectionId, fields] of Object.entries(SECTION_FIELDS)) {
      if (fields.length === 0) continue;
      const applicableFields = fields.filter((field) => {
        if (field === "military_certificate_file" && gender !== "male") return false;
        if (field === "enrollment_verification_file" && !isStudent) return false;
        if ((field === "enrollment_start_date" || field === "enrollment_end_date") && !isStudent) return false;
        if ((field === "diploma_file" || field === "diploma_transcript_file") && noEducation) return false;
        if (field === "work_proof_file" && noExperience) return false;
        return true;
      });
      if (applicableFields.length === 0) continue;
      const allFilled = applicableFields.every((field) => {
        const val = watchedValues[field];
        if (val instanceof File) return true;
        if (draftFilenames[field] != null) return true;
        if (val === null || val === undefined || val === "") return false;
        if (Array.isArray(val)) {
          const isStringArray = val.every((item) => typeof item === "string");
          if (isStringArray) {
            return val.length > 0 && val.every((item) => typeof item === "string" && item.trim() !== "");
          }
          const hasLocalFile = val.some((item) => item instanceof File);
          const hasDraftFile = Object.keys(draftFilenames).some((k) => k.startsWith(field));
          return hasLocalFile || hasDraftFile;
        }
        return true;
      });
      if (allFilled) completed.push(sectionId);
    }
    return completed;
  }, [watchedValues, draftFilenames]);

  const errorSteps = useMemo(() => {
    if (!formState.isSubmitted) return [];
    const errorSections: string[] = [];
    for (const [sectionId, fields] of Object.entries(SECTION_FIELDS)) {
      const hasError = fields.some((field) => field in formState.errors);
      if (hasError) errorSections.push(sectionId);
    }
    return errorSections;
  }, [formState.errors, formState.isSubmitted]);

  const stepHighlights = useMemo(() => {
    if (!hasTakenField || errorSteps.includes("personal")) return errorSteps;
    return [...errorSteps, "personal"];
  }, [errorSteps, hasTakenField]);

  function navigateToFlatStep(index: number) {
    const step = FLAT_STEPS[index];
    setActiveSection(step.section);
    if (step.section === "contract" && step.tab) {
      setContractTab(step.tab as ContractTab);
    }
    if (step.section === "documents" && step.tab) {
      setDocumentsTab(step.tab as DocumentTab);
    }
  }

  function handleNext() {
    if (!isLastStep) {
      navigateToFlatStep(flatIndex + 1);
    }
  }

  function handleBack() {
    if (!isFirstStep) {
      navigateToFlatStep(flatIndex - 1);
    }
  }

  function handleStepChange(id: string) {
    setActiveSection(id as ImportWorkerSectionId);
    if (id === "contract") setContractTab("info");
    if (id === "documents") setDocumentsTab("contract");
  }

  const onSubmit = useCallback(
    async function onSubmit(data: ImportWorkerFormValues) {
      if (isSubmitting) return;
      try {
        setIsSubmitting(true);
        const formData = buildImportFormData(data);
        const submitDraftId = draftIdRef.current ?? draftId;
        if (submitDraftId) formData.append("draft_id", String(submitDraftId));
        await importWorker(formData).unwrap();
        methods.reset(DEFAULT_FORM_VALUES);
        setIsSubmitted(true);
        setPrompt({
          title: t("messages.success.title"),
          text: t("messages.success.text"),
          variant: "success",
        });
      } catch (err: unknown) {
        const fieldData = (err as { data?: Record<string, unknown> })?.data;
        if (fieldData && Array.isArray(fieldData.contract_number)) {
          methods.setError("contract_number", { message: t("validation.contract_number_taken") });
          setActiveSection("contract");
          setContractTab("info");
          return;
        }
        const error = err as { status?: number };
        const conflictKey = getWorkerConflictKey(err);
        const text = conflictKey
          ? tCommon(conflictKey)
          : error.status === 409
            ? t("messages.error.iinConflict")
            : extractErrorMessage(err, "messages.error.defaultText", t);
        setPrompt({
          title: t("messages.error.title"),
          text,
          variant: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [importWorker, isSubmitting, t, tCommon, draftId],
  );

  async function handleFormSubmit() {
    const valid = await trigger();
    if (!valid) {
      const firstErrorSection = IMPORT_SECTIONS.find((section) => {
        const fields = SECTION_FIELDS[section.id];
        return fields.some((field) => field in formState.errors);
      });
      if (firstErrorSection) {
        setActiveSection(firstErrorSection.id);
        if (firstErrorSection.id === "contract") {
          const errorTab = (Object.keys(CONTRACT_TAB_FIELDS) as ContractTab[]).find((tab) =>
            CONTRACT_TAB_FIELDS[tab].some((field) => field in formState.errors),
          );
          if (errorTab) setContractTab(errorTab);
        }
        if (firstErrorSection.id === "documents") {
          const errorTab = (Object.keys(DOCUMENT_TAB_FIELDS) as DocumentTab[]).find((tab) =>
            DOCUMENT_TAB_FIELDS[tab].some((field) => field in formState.errors),
          );
          if (errorTab) setDocumentsTab(errorTab);
        }
      }
      return;
    }
    handleSubmit(onSubmit)();
  }

  function handlePromptClose() {
    if (prompt?.variant === "success") {
      setPrompt(null);
      navigate(HR_EMPLOYEES_PAGE_ROUTE, { replace: true });
      return;
    }
    setPrompt(null);
  }

  if (draftId != null && isDraftLoading) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <ImportWorkerPageSkeleton />
      </>
    );
  }

  return (
    <FormProvider {...methods}>
      <title>{t("meta.title")}</title>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={handlePromptClose}
          namespace="ImportWorkerPage"
        />
      )}

      {leaveProtection.modalOpen && (
        <PromptForm
          title={t("leaveModal.title")}
          text={t("leaveModal.description")}
          variant="warning"
          onClose={leaveProtection.cancelNavigation}
          onConfirm={leaveProtection.confirmNavigation}
          confirmText={t("leaveModal.leave")}
          cancelText={t("leaveModal.stay")}
          namespace="ImportWorkerPage"
        />
      )}

      {(draftSaveStatus === "saved" || draftSaveStatus === "error") && (
        <div className="fixed bottom-6 right-6 z-50">
          <Toast
            key={draftSaveStatus}
            color={draftSaveStatus === "saved" ? "positive" : "negative"}
            text={draftSaveStatus === "saved" ? t("draft.saved") : t("draft.saveFailed")}
            autoCloseDuration={5000}
            onClose={handleDraftStatusReset}
          />
        </div>
      )}

      <section className="flex-1 min-w-0">
        <div className="mb-7 flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke">
          <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
          <Button type="button" variant="secondary" onClick={handleSaveDraft} disabled={isSavingDraft || isSubmitting}>
            {isSavingDraft ? t("draft.saving") : t("draft.saveDraft")}
          </Button>
        </div>

        <StepsSwitcher
          steps={translatedSections}
          activeStep={activeSection}
          onStepChange={handleStepChange}
          scrollToTopOnChange
          completedSteps={completedSteps}
          errorSteps={stepHighlights}>
          {activeSection === "personal" && (
            <PersonalSection
              availability={{
                iin: iinAvailability.state,
                email: emailAvailability.state,
                phone: phoneAvailability.state,
              }}
            />
          )}
          {activeSection === "contract" && (
            <ContractSection activeTab={contractTab} onTabChange={setContractTab} contractNumberError={contractNumberError} />
          )}
          {activeSection === "banking" && <BankingSection />}
          {activeSection === "documents" && <DocumentsSection activeTab={documentsTab} onTabChange={setDocumentsTab} draftFilenames={draftFilenames} />}

          <div className="flex justify-end gap-3 mt-6">
            {!isFirstStep && (
              <Button type="button" variant="secondary" onClick={handleBack}>
                {t("buttons.back")}
              </Button>
            )}

            {!isLastStep && (
              <Button type="button" variant="primary" onClick={handleNext}>
                {t("buttons.next")}
              </Button>
            )}

            {isLastStep &&
              (hasTakenField ? (
                <OnboardingTooltip
                  direction="top"
                  align="right"
                  content={
                    <p className="text-body-regular-sm content-static-white">{t("uniqueness.submitBlockedHint")}</p>
                  }>
                  <Button type="button" variant="primary" onClick={handleFormSubmit} disabled>
                    {t("buttons.submit")}
                  </Button>
                </OnboardingTooltip>
              ) : (
                <Button type="button" variant="primary" onClick={handleFormSubmit} disabled={isSubmitting}>
                  {isSubmitting ? t("buttons.loading") : t("buttons.submit")}
                </Button>
              ))}
          </div>
        </StepsSwitcher>
      </section>
    </FormProvider>
  );
}
